/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-02-14 00:00:00
 *
 *******************************************************/
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { matchPath } from 'react-router';
import { Modal, Card, Tabs } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { routes } from 'src/routes';
import history from 'src/routes/history';

import AddByYouContent from 'src/components/containers/shopify-integration/modal/added-by-you';
import BoostTemplatesContent from 'src/components/containers/shopify-integration/modal/boost-templates';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';

import { LIMIT_CUSTOM_TEMPLATE_INSTANCE } from 'src/constants/shopify-integration';

type Props = {};
type ComposedProps = Props & WithI18nProps;

type State = {};

class SelectTemplateModal extends Component<ComposedProps, State> {
  state = {
    selected: 0,
    selectedTemplateId: '',
    selectedBoostTemplateId: '',
    themeNameLib: ''
  };

  render() {
    const { open, templateInstances = [], disabling, secondaryAction = '', i18n } = this.props;
    const { selected } = this.state;

    return (
      <div className="select-template-modal">
        <Modal
          open={open}
          onClose={this.onClose}
          title={i18n.translate('AdminHTML.ShopifyIntegration.ProgressBar.step1')}
          primaryAction={[
            {
              content: i18n.translate('AdminHTML.ShopifyIntegration.Actions.confirm'),
              onAction: this.handleConfirmModal,
              loading: disabling,
              disabled: selected === 1 && templateInstances.length >= LIMIT_CUSTOM_TEMPLATE_INSTANCE
            }
          ]}
          secondaryActions={
            secondaryAction === 'cancel'
              ? [
                  {
                    content: i18n.translate('AdminHTML.ShopifyIntegration.Actions.cancel'),
                    onAction: this.onClose
                  }
                ]
              : templateInstances.length > 0
              ? [
                  {
                    content: i18n.translate('AdminHTML.ShopifyIntegration.Actions.manageTemplate'),
                    onAction: this.handleGoToManageTemplatePage
                  }
                ]
              : ''
          }>
          {/* Content */}
          {templateInstances.length > 0
            ? this.renderTabsContent()
            : this.renderBoostTemplateContent()}
        </Modal>
      </div>
    );
  }

  renderTabsContent = () => {
    const {
      i18n,
      integratedTemplateInstance = {},
      templateInstances = [],
      boostTemplates = []
    } = this.props;
    const { selected } = this.state;

    const tabs = [
      {
        id: 'tabAddedByYou',
        content: (
          <p>
            <span className="total-instance">{templateInstances.length}</span>
            {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplateModal.tabAddedByYou')}
          </p>
        ),
        accessibilityLabel: i18n.translate(
          'AdminHTML.ShopifyIntegration.SelectTemplateModal.tabAddedByYou'
        ),
        panelID: 'tabAddedByYou'
      },
      {
        id: 'boostTemplates',
        content: i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplateModal.boostTemplates'),
        panelID: 'boostTemplates'
      }
    ];

    const tabComponents = [
      <AddByYouContent
        integratedTemplateInstance={integratedTemplateInstance}
        templateInstances={templateInstances}
        handleSelectTemplate={this.handleSelectTemplate}
      />,
      <BoostTemplatesContent
        boostTemplates={boostTemplates}
        handleSelectTemplate={this.handleSelectTemplate}
        isReachLimitCustomTemplate={templateInstances.length >= LIMIT_CUSTOM_TEMPLATE_INSTANCE}
      />
    ];

    return (
      <div className="select-template-modal">
        <Card>
          <Tabs tabs={tabs} selected={selected} onSelect={this.handleTabChange}>
            <Card.Section>{tabComponents[selected]}</Card.Section>
          </Tabs>
        </Card>
      </div>
    );
  };

  renderBoostTemplateContent = () => {
    const { boostTemplates = [] } = this.props;

    return (
      <div style={{ padding: '20px 20px 24px 20px' }}>
        <BoostTemplatesContent
          boostTemplates={boostTemplates}
          handleSelectTemplate={this.handleSelectTemplate}
        />
      </div>
    );
  };

  handleTabChange = selectedTabIndex => {
    this.setState({ selected: selectedTabIndex });
  };

  handleSelectTemplate = (templateId, themeNameLib, isBoostTemplate) => {
    if (isBoostTemplate) {
      this.setState({ selectedBoostTemplateId: templateId, themeNameLib });
    } else {
      this.setState({ selectedTemplateId: templateId, themeNameLib });
    }
  };

  handleConfirmModal = () => {
    const { onConfirm, templateInstances } = this.props;
    const { selectedTemplateId, selectedBoostTemplateId, selected, themeNameLib } = this.state;

    // Submit with store template instance
    if (!selected && templateInstances.length > 0) {
      return onConfirm(selectedTemplateId, themeNameLib, false);
    }

    // Submit with Boost default template
    onConfirm(selectedBoostTemplateId, themeNameLib, true);
  };

  handleGoToManageTemplatePage = () => {
    const {
      selectedTheme = {},
      integratedTemplateInstance = {},
      updateMultiFieldsReducer,
      match
    } = this.props;
    const themeRole = _.get(selectedTheme, 'role', '');
    const isLiveTheme = themeRole === 'main';

    updateMultiFieldsReducer({
      currentTemplateId: integratedTemplateInstance.templateId,
      isLiveTheme,
      isIsNewTemplate: !!matchPath(match.path, {
        path: routes.shopifyIntegration.newThemeWidget(':themeId'),
        exact: true,
        strict: false
      })
    });

    history.push({
      pathname: routes.shopifyIntegration.manageTemplateInstance()
    });
  };

  onClose = () => {
    const { onClose } = this.props;
    this.setState({ selected: 0 });
    onClose();
  };
}

const mapStateToProps = (state, props) => {
  const { templateInstance = {} } = state;

  return {
    getTemplateInstancesLoading: _.get(templateInstance, 'getTemplateInstancesLoading', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateMultiFieldsReducer: data => dispatch(themeIntegrationAction.handleUpdateMultiFields(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SelectTemplateModal));
